import { initializeApp } from "firebase/app";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  connectFirestoreEmulator, // Import Firestore emulator connection
  // Note: If you need query functionality later, import things like:
  // collection, query, where, getDocs, serverTimestamp
} from "firebase/firestore";
import {
  getAuth,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  // Note: If you need other auth methods, import them, e.g.:
  createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification, onAuthStateChanged, signOut
} from "firebase/auth";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions"; // Import v9 functions service, callable, and emulator connection
import { connectAuthEmulator } from "firebase/auth"; // Import Auth emulator connection
// import { getAnalytics } from "firebase/analytics"; // Uncomment if you need Analytics

// Your web app's Firebase configuration
// Consider moving this to environment variables for security
const firebaseConfig = {
  apiKey: "AIzaSyAvMtbM46GZ6kAfUy6JCY0LpJreh_iH0S4", // IMPORTANT: Keep API keys secure, ideally via environment variables
  authDomain: "prod-entretien-tech.firebaseapp.com",
  databaseURL: "https://prod-entretien-tech.firebaseio.com", // Note: databaseURL is for Realtime Database, not Firestore
  projectId: "prod-entretien-tech",
  storageBucket: "prod-entretien-tech.appspot.com",
  messagingSenderId: "160706187404",
  appId: "1:160706187404:web:a1e7bbf8472ec4940baba9",
  measurementId: "G-D2Z4TE2HZJ",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Connect to emulators if in development mode
try {
  console.log("Firebase app initialized successfully.");
  // Safe check for development mode that works in browsers
  const isDevelopment = typeof process !== 'undefined' &&
                        process?.env?.NODE_ENV === 'development' ||
                        window.location.hostname === 'localhost';
  
  if (isDevelopment) {
    console.log('Connecting to Firebase Emulators...');
    // Default ports: Firestore 8080, Functions 5001
    try {
      connectFirestoreEmulator(getFirestore(app), 'localhost', 8080);
      connectFunctionsEmulator(getFunctions(app), 'localhost', 5001);
      // Note: connectAuthEmulator requires the host to include the scheme (http://)
      connectAuthEmulator(getAuth(app), 'http://localhost:9099', {
        disableWarnings: true,
      });
      // Add other emulators if needed, e.g.:
      // connectStorageEmulator(getStorage(app), 'localhost', 9199);
    } catch (e) {
      console.error("Failed to connect to Firebase Emulators:", e);
    }
  }

} catch (err) {
  console.log(err)
}


// Initialize and export services
export const auth = getAuth(app);
export const firestore = getFirestore(app);
export const functions = getFunctions(app); // Initialize and export functions service
// export const analytics = getAnalytics(app); // Uncomment if using Analytics

// --- Auth Providers ---
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

const facebookProvider = new FacebookAuthProvider();
// Add scopes if needed: facebookProvider.addScope('email');

// --- Auth Helper Functions ---
export const signInWithGoogle = () => signInWithPopup(auth, googleProvider);
export const signInWithFacebook = () => signInWithPopup(auth, facebookProvider);
export { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification, signOut }; // Add signOut

// --- Firestore Helper Functions ---

/**
 * Creates or updates a user profile document in Firestore.
 * @param {object} userAuth - The user object from Firebase Auth.
 * @param {object} additionalData - Any additional data to merge into the profile.
 * @returns {object} Firestore DocumentReference
 */
export const createUserProfileDocument = async (userAuth, additionalData) => {
  if (!userAuth) return;

  // Use doc() function from v9 SDK
  const userRef = doc(firestore, "users", userAuth.uid);

  try {
    // Use getDoc() function from v9 SDK
    const snapshot = await getDoc(userRef);

    if (!snapshot.exists()) {
      const { displayName, email, photoURL } = userAuth;
      const createdAt = new Date(); // Use JS Date object, Firestore handles timestamp conversion

      try {
        // Use setDoc() function from v9 SDK
        await setDoc(userRef, {
          displayName,
          email,
          createdAt, // Use standard JS Date
          photoURL,
          isPremium: false, // Default premium status
          premiumType: 'none', // Default premium type
          credits: 4, // Default credits for new users
          hasUsedShowcaseSession: false, // Default showcase status
          ...additionalData,
        });
      } catch (err) {
        console.error("Error creating user profile:", err.message);
        // Consider throwing the error or handling it more robustly
      }
    }
    // If snapshot exists, you might want to update it here if needed
    // else { await updateDoc(userRef, { lastLogin: new Date(), ...additionalData }); }

  } catch (error) {
    console.error("Error accessing user document:", error);
  }

  return userRef; // Return the DocumentReference
};

/**
 * Converts a Firestore QuerySnapshot (questions) to a mapped array.
 * Assumes the input is a QuerySnapshot from getDocs().
 * @param {object} questionsSnapshot - Firestore QuerySnapshot.
 * @returns {Array} Array of question objects.
 */
export const convertQuestionsSnapshotToMap = (questionsSnapshot) => {
  return questionsSnapshot.docs.map((docSnapshot) => {
    const {
      title,
      answer,
      questionDetailId,
      score,
      isCodeChallenge,
      // Add other fields from your schema as needed
      libelle, // Example if 'libelle' is used instead of 'title'
    } = docSnapshot.data();

    return {
      id: docSnapshot.id, // Use 'id' consistently
      title: title || libelle, // Use title or libelle
      answer,
      questionDetailId,
      score,
      isCodeChallenge,
    };
  });
};

/**
 * Converts a Firestore QuerySnapshot (topics) to a mapped array.
 * Assumes the input is a QuerySnapshot from getDocs().
 * @param {object} topicsSnapshot - Firestore QuerySnapshot.
 * @returns {Array} Array of topic objects.
 */
export const convertQuestionsTopicsSnapshotToMap = (topicsSnapshot) => {
  return topicsSnapshot.docs.map((docSnapshot) => {
    const { topic, name, qtyTodo, qtyDone, categories } = docSnapshot.data();
    return {
      id: docSnapshot.id,
      topic,
      name,
      qtyTodo,
      qtyDone,
      categories,
    };
  });
};

// No longer need to export the old 'firebase' object
// export default firebase; // REMOVED
